/**
 * 订阅计划管理器
 * 套餐定价列表、创建订阅、购买积分包
 * @author Joey
*/

import { useEffect, useState } from "react";
import { v4 as uuid } from 'uuid';
import { FeatureAllData } from "@/services/feature-all";
import { miutaPrice } from "@/services/miuta-prices";
import { canUseWindow } from "@/utils/canuseWindow";
import { iamAccountHooks } from "./iam-account-manager";
import { createSubscription, CreateSubscriptionData, CreateSubscriptionRequest } from "@/services/pay-create-subscription";
import { showToastByCode } from '@/hooks/use-toast';
import { encryptParams } from "@/utils/encrypt-util";
import { pollUntilCompleted } from "@/utils/pollUntilTaskCompleted";
import { subscriptionManager } from "./subscription-manager";
import { useDecryptRouteData } from "@/hooks/use-decrypt-route-data";
import { payCreateOneBuy, PayCreateOneBuyData } from "@/services/pay-create-one-buy";
import analytics from "@/utils/analytics";
import { CREDITS_PACKAGES } from "@/components/settings-subscription/constants";

type PlansChangeEventData = { plans: Plan[] };
type PlansChangeEvent = CustomEvent<PlansChangeEventData>;

export interface Plan {
  id: string;
  itemName: string;
  itemCode: string;
  price: number;
  isStarter: boolean;
  isPro: boolean;
  isPremium: boolean;
  featureCode: string;
}

// 套餐类型
export const PRICE_PLAN_TYPE = {
  item_miuta_starter: 'item_miuta_starter',
  item_miuta_pro: 'item_miuta_pro',
  item_miuta_premium: 'item_miuta_premium',
} as const;

/** 套餐对应的权益代码 */
export const FEATURE_CODE = {
  item_miuta_starter: 'miuta_starter',
  item_miuta_pro: 'miuta_pro',
  item_miuta_premium: 'miuta_premium',
} as const;

/** 会员类型 */
export const VIP_TYPE = {
  /** 不是会员 */
  not: -1,
  /** 是会员 */
  yes: 0,
  /** 会员过期 */
  expired: 1,
} as const;

/** 是否连续订阅 */
export const IS_RENEWS = {
  /** 已取消连续订阅 */
  not: -1,
  /** 连续订阅中 */
  yes: 1,
} as const;

/** 支付成功或失败的回调参数 */
export interface PaymentCallbackParams {
  sku: string,
  success: string,
  fail: string,
  type: 'plan' | 'credits'
}

class SubscriptionPlanManager {
  private static instance: SubscriptionPlanManager;
  private readonly STORAGE_KEY = "MIUTA_SUBSCRIPTION_PLAN";
  private readonly CREATE_SUBSCRIPTION_KEY = "MIUTA_CREATE_SUBSCRIPTION_KEY";
  // 套餐定价列表
  private plans: Plan[] = [];
  // 创建订阅的key
  private createSubscriptionKey: string = '';

  // 单例模式
  public static getInstance(): SubscriptionPlanManager {
    if (!SubscriptionPlanManager.instance) {
      SubscriptionPlanManager.instance = new SubscriptionPlanManager();
    }
    return SubscriptionPlanManager.instance;
  }

  // 初始化
  public async init() {
    try {
      const cachedKey = localStorage.getItem(this.CREATE_SUBSCRIPTION_KEY);
      console.log('创建订阅的缓存key：', { cachedKey })
      if (cachedKey) {
        this.createSubscriptionKey = cachedKey;
      }
      const res = await this.fetchPlans();
      return res;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  // 获取套餐定价列表
  public async fetchPlans() {
    try {
      const plans = await miutaPrice();
      // 每个套餐的权益
      const buildPlans = plans.map(plan => {
        return {
          id: plan.itemCode,
          itemName: plan.itemName,
          itemCode: plan.itemCode,
          price: plan.price,
          isStarter: plan.itemCode === PRICE_PLAN_TYPE.item_miuta_starter,
          isPro: plan.itemCode === PRICE_PLAN_TYPE.item_miuta_pro,
          isPremium: plan.itemCode === PRICE_PLAN_TYPE.item_miuta_premium,
          // 套餐对应的权益代码！
          featureCode: FEATURE_CODE[plan.itemCode],
        }
      })
      this.setPlans(buildPlans);
      return buildPlans;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  // 创建订阅
  public async createSubscription(type: 'plan' | 'credits', params: CreateSubscriptionRequest) {
    try {
      let res: CreateSubscriptionData | PayCreateOneBuyData;
      if (type === 'plan') {
        // 创建订阅（套餐）
        res = await createSubscription(params)
      } else if (type === 'credits') {
        // 创建一次性订阅（积分包）
        res = await payCreateOneBuy(params)
      }
      if (res.url) {
        window.location.href = res.url;
      }
    } catch (error) {
      console.log(error)
      throw error
    }
  }

  // 清除信息
  public clear(): void {
    this.clearCreateSubscriptionKey();
  }
  public clearCreateSubscriptionKey(): void {
    this.createSubscriptionKey = null;
    localStorage.removeItem(this.CREATE_SUBSCRIPTION_KEY);
  }

  // ========================= 读取写入 ==========================
  public getPlans() {
    return this.plans;
  }
  public setPlans(plans: Plan[]) {
    this.plans = plans;
    this.dispatchPlansChange(plans);
  }

  public getCreateSubscriptionKey() {
    if (!this.createSubscriptionKey) {
      const cachedKey = localStorage.getItem(this.CREATE_SUBSCRIPTION_KEY);
      if (cachedKey) {
        this.createSubscriptionKey = cachedKey;
      }
    }
    return this.createSubscriptionKey;
  }
  public setCreateSubscriptionKey(key: string) {
    this.createSubscriptionKey = key;
    try {
      localStorage.setItem(this.CREATE_SUBSCRIPTION_KEY, key);
    } catch (error) {
      console.log(error);
    }
  }

  // ========================== 通知事件 ==========================
  // 监听订阅计划列表变化
  public onPlansChange(listener: (e: PlansChangeEvent) => void) {
    if (canUseWindow()) {
      window.addEventListener(`${this.STORAGE_KEY}:plans`, listener);

      return () => {
        window.removeEventListener(`${this.STORAGE_KEY}:plans`, listener);
      };
    }
  }
  private dispatchPlansChange(plans: Plan[]) {
    if (canUseWindow()) {
      const event = new CustomEvent<PlansChangeEventData>(
        `${this.STORAGE_KEY}:plans`,
        { detail: { plans } }
      );
      window.dispatchEvent(event);
    }
  }
}

//========================== Hooks ==================================
// 套餐订阅计划
export const useSubscriptionPlan = () => {
  const [loading, setLoading] = useState(false)
  const [plans, setPlans] = useState(subscriptionPlanManager.getPlans())

  const init = async () => {
    setLoading(true)
    try {
      await subscriptionPlanManager.init()
    } catch (error) {
      console.log(error)
      showToastByCode(error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // 如果已经初始化过且有数据，不再重复初始化
    if (!plans.length) {
      init()
    }
  }, [plans.length])

  useEffect(() => {
    const off = subscriptionPlanManager.onPlansChange((e) => {
      setPlans(e.detail.plans)
    })
    return () => off()
  }, [])

  return {
    plans,
    loading,
  }
}

// 创建订阅
const useCreateSubscription = () => {
  const [loading, setLoading] = useState(false)
  const { account } = iamAccountHooks.useData()
  const plans = subscriptionPlanManager.getPlans()

  // 创建订阅
  const create = async ({
    sku,
    cancelSub,
    type
  }: {
    sku: string,
    cancelSub?: string,
    type?: 'plan' | 'credits'
  }) => {
    try {
      setLoading(true)
      const newKey = uuid()
      subscriptionPlanManager.setCreateSubscriptionKey(newKey)

      // 基本支付回调参数
      const params: Omit<PaymentCallbackParams, 'success' | 'fail'> = {
        sku,
        type
      }

      // 构建 success 参数对象并加密
      const encryptedSuccess = encryptParams({ ...params, success: newKey });

      // 构建 fail 参数对象并加密
      const encryptedFail = encryptParams({ ...params, fail: newKey });

      // 创建订阅
      await subscriptionPlanManager.createSubscription(type, {
        sku,
        cancelSub,
        teamCode: account.teamCode || '',
        email: account?.email,
        successJumpUrl: location.origin + location.pathname + `?k=${encryptedSuccess}`,
        failJumpUrl: location.origin + location.pathname + `?k=${encryptedFail}`,
      })

      // 购买订阅套餐埋点
      if (type === 'plan' && !cancelSub) {
        analytics.trackPlanSelectClicked({
          user_email: account?.email,
          plan_id: sku,
          price: plans?.find((item) => item.itemCode === sku)?.price || 0,
          currency: 'usd',
        })
      }

      // 用户主动升级/降级套餐埋点
      if (type === 'plan' && cancelSub) {
        analytics.trackSubscriptionUpgraded({
          user_email: account?.email,
          from_plan: account?.nowVipType,
          to_plan: sku,
        })
      }

      // 购买积分包埋点
      if (type === 'credits') {
        analytics.trackCreditPurchased({
          user_email: account?.email,
          pack_id: sku,
          price: CREDITS_PACKAGES.find((item) => item.sku === sku)?.price || 0,
          credits: CREDITS_PACKAGES.find((item) => item.sku === sku)?.credits || 0,
          payment_method: 'stripe',
        })
      }

    } catch (error) {
      console.log(error)
      subscriptionPlanManager.clearCreateSubscriptionKey()
      showToastByCode(error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  return {
    create,
    loading,
  }
}

// 轮询权益列表
const usePollFeature = () => {
  const [polling, setPolling] = useState(false)

  const startPolling = async (sku: string) => {
    setPolling(true)
    try {
      const buyPlan = await pollUntilCompleted(async () => {
        // 请求权益列表
        const features = await subscriptionManager.fetchFeatures()
        // 检查权益列表是否包含指定套餐的权益，并且 pid 是 1001，是则停止轮询
        const matchPlan = features.find((item) => item.featureCode === FEATURE_CODE[sku])
        if (matchPlan && matchPlan.pid === 1001) {
          console.log('找到购买的权益套餐了！')
          return matchPlan
        }
        return null
      }, {
        delay: 2000,
        maxPollingCount: 30,
      })
      console.log('轮询结束')
      setPolling(false)
      return buyPlan;
    } catch (error) {
      console.log(error)
      throw error
    } finally {
      setPolling(false)
    }
  }

  return {
    startPolling,
    polling,
  }
}

// 处理支付成功或失败的回调
const usePaymentStatusModal = () => {
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showFailModal, setShowFailModal] = useState(false)
  const [buyPlan, setBuyPlan] = useState<FeatureAllData>()

  const oldKey = subscriptionPlanManager.getCreateSubscriptionKey()

  const { create, loading: creating } = useCreateSubscription()
  const { polling, startPolling } = usePollFeature()

  // 获取路由参数
  const { data } = useDecryptRouteData<PaymentCallbackParams>('k');

  const handlePayStatus = async () => {
    // 没有订阅key
    if (!oldKey) return
    // 路由参数没有sku
    if (!data?.sku) return

    // 支付成功，轮询请求权益列表，检查是否包含指定套餐的权益
    if (oldKey === data?.success) {
      console.log('支付成功，轮询请求权益列表，检查是否包含指定套餐的权益...')
      try {
        const res = await startPolling(data.sku)
        console.log('套餐到账！')
        setBuyPlan(res)
        setShowSuccessModal(true)
      } catch (error) {
        console.log(error)
        setShowFailModal(true)
      }
    }
    // 支付失败
    else if (oldKey === data?.fail) {
      setShowFailModal(true)
    }
  }

  useEffect(() => {
    handlePayStatus()
  }, [location.pathname])

  const onSuccessModalOpenChange = (open: boolean) => {
    setShowSuccessModal(open)
  }

  const onFailModalOpenChange = (open: boolean) => {
    setShowFailModal(open)
    subscriptionPlanManager.clearCreateSubscriptionKey()
    window.location.href = location.origin + location.pathname
  }

  // 失败重试
  const onRetry = async () => {
    if (data?.sku) {
      await create({ sku: data.sku, type: data.type })
    }
    setShowFailModal(false)
  }

  return {
    showSuccessModal,
    showFailModal,
    onSuccessModalOpenChange,
    onFailModalOpenChange,
    onRetry,
    creating,
    polling,
    buyPlan,
  }
}

export const subscriptionPlanHooks = {
  useSubscriptionPlan,
  useCreateSubscription,
  usePaymentStatusModal,
  usePollFeature,
}

export const subscriptionPlanManager = SubscriptionPlanManager.getInstance();
